import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from "axios";
import utils from "../../utils";
import { Container, Box, Typography, Divider, Drawer, Button, TextField, IconButton, Tooltip, Snackbar, Badge } from "@mui/material";
import FeedbackIcon from '@mui/icons-material/Feedback';
import { Alert } from '@mui/material';
import NotificationPermissionRequest from '../NotificationRequest';
import DownloadIcon from '@mui/icons-material/Download';

function Cli() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [feedback, setFeedback] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [ragioneSociale, setRagioneSociale] = useState("");
  const [meId, setMeId] = useState(0);
  const [statoAvvisi, setStatoAvvisi] = useState('Pagato');
  const [visibilita, setVisibilita] = useState('');

  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);
  const [showNotificationReminder, setShowNotificationReminder] = useState(false);

  const linkContentMap = {
    contrattualistica: <h2>Contrattualistica</h2>,
    amministrazione: <h2>Amministrazione</h2>,
    paghe: <h2>Paghe</h2>,
    enti: <h2>Enti</h2>,
    prenotasala: <h2>Sala Riunioni</h2>,
    prenotacba: <h2>Prenota CBA</h2>,
    previsionecosti: <h2>Previsione Costi</h2>,
    guida: <h2>Guida Software</h2>,
    domande: <h2>Domande</h2>,
  };

  function renderLink(key) {
    if (visibilita != 'all' & !visibilita.split(',').map(v => v.split(':')[0]).includes(key)) {
      return null;
    }
    const content = linkContentMap[key];
    if (!content) return null;

    const className = 'clienti-card';
    const to = `/cli/${key}`;

    return (
      <Link key={key} to={to} className={className}>
        {content}
      </Link>
    );
  }

  const checkNotificationPermission = () => {
    try {
      if ('Notification' in window) {
        if (Notification && Notification.permission === 'granted') {
          setShowNotificationReminder(false);
        } else if (Notification && Notification.permission !== 'denied') {
          setShowNotificationReminder(true);
        }
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    checkNotificationPermission();
  }, []);

  useEffect(() => {
    const handlePermissionChange = () => {
      checkNotificationPermission();
    };

    window.addEventListener('permissionchange', handlePermissionChange);
    return () => {
      window.removeEventListener('permissionchange', handlePermissionChange);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const installApp = () => {
    if (installEvent) {
      installEvent.prompt();
      installEvent.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          console.log('Utente ha accettato l’installazione');
          setShowInstallPrompt(false);
          localStorage.setItem('installPromptShown', 'true');
        } else {
          console.log('Utente ha rifiutato l’installazione');
        }
        setInstallEvent(null);
      });
    } else {
      alert("Installazione non disponibile o app già installata.");
      setShowInstallPrompt(false);
      localStorage.setItem('installPromptShown', 'true');
    }
  };

  useEffect(() => {
    loadData();
    saveAttivita();
    fetchStatoAvvisi();

    // Controlla se i prompt sono già stati mostrati
    const installPromptShown = localStorage.getItem('installPromptShown');
    if (!installPromptShown) {
      setShowInstallPrompt(true);
    }

    const notificationReminderShown = localStorage.getItem('notificationReminderShown');
    if (!notificationReminderShown) {
      setShowNotificationReminder(true);
    }
  }, []);

  const getDotColor = (stato) => {
    switch (stato) {
      case 'Pagato': return '#ffffff'; // White
      case 'Da Pagare': return '#ffeb3b'; // Yellow
      case 'In Scadenza': return '#ff9800'; // Orange
      case 'Scaduto': return '#f44336'; // Red
      default: return '#ccc'; // Gray
    }
  };

  const loadData = async () => {
    try {
      const res = await axios.get("/api/me", {
        headers: utils.getAuthHeaders(),
        validateStatus: () => true,
      });
      if (res.status === 200) {
        const { success, me } = res.data;
        if (success && me.op) {
          window.location.href = "/op";
          return;
        }
        if (success) {
          setRagioneSociale(me.ragione_sociale || ""); // Imposta la ragione sociale
          setVisibilita(me.visibilita);
          setMeId(me.id);
          return; // stay on the page
        }
      }
    } catch (_) { }
    if (process.env.REACT_APP_ENV !== "test") {
      window.location.href = "/login";
    }
  };

  const saveAttivita = async () => {
    try {
      await axios.post("/api/saveattivo", {}, {
        headers: utils.getAuthHeaders(),
      });
    } catch (_) {
      console.error("Error saving activity");
    }
  };

  const fetchStatoAvvisi = async () => {
    try {
      const res = await axios.get("/api/statoavvisicli", {
        headers: utils.getAuthHeaders(),
        validateStatus: () => true,
      });
      if (res.status === 200 && res.data.success) {
        setStatoAvvisi(res.data.stato);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleFeedbackSubmit = async () => {
    try {
      const res = await axios.post("/api/feedback", { feedback }, {
        headers: utils.getAuthHeaders(),
      });
      if (res.status === 200 && res.data.success) {
        setFeedback("");
        setDrawerOpen(false);
        setSnackbarMessage("Feedback inviato con successo!");
        setSnackbarSeverity("success");
      } else {
        console.error("Error submitting feedback", res.data.error);
        setSnackbarMessage("Errore nell'invio del feedback.");
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error("Error submitting feedback", error);
      setSnackbarMessage("Errore nell'invio del feedback.");
      setSnackbarSeverity("error");
    } finally {
      setOpenSnackbar(true);
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const [displayedText, setDisplayedText] = useState("");

  useEffect(() => {
    const fullText = "La tua area personale per gestire servizi e informazioni";
    let index = 0;
    const interval = setInterval(() => {
      setDisplayedText(fullText.slice(0, index + 1));
      index++;
      if (index === fullText.length) clearInterval(interval);
    }, 30); // Velocità del "typing"
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <NotificationPermissionRequest />
      <style>
        {`
    .clienti-area {
      display: flex;
      justify-content: center;
    }

    .clienti-card {
      border: 1px solid #ccc;
      padding: 16px;
      margin: 16px;
      text-align: center;
      min-width: 200px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.5);
      transition: transform 0.2s;
      text-decoration: none;
      color: black;
      border-radius: 10px;
      position: relative;
    }

    .clienti-card-amm {
      border: 1px solid #ccc;
      padding: 16px;
      margin: 16px;
      text-align: center;
      min-width: 200px;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.5);
      transition: transform 0.2s;
      text-decoration: none;
      color: black;
      background-color: ${getDotColor(statoAvvisi)};
      border-radius: 10px;
      position: relative;
    }

    .clienti-card:hover {
      transform: scale(1.05);
    }

    @media (max-width: 768px) {
      .clienti-card {
        min-width: auto;
        width: 80%;
        font-size: 16px;
      }

      .clienti-area {
        flex-direction: column;
        padding: 20px;
        margin-top: 2vh;
      }
    }

    .feedback-button {
      position: fixed;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      z-index: 1; /* Ensure it's above other elements */
    }

    .accounting-button {
      position: relative;
      left: 10px;
      bottom: 0px;
      z-index: 1;
      background-color: #ffeb3b;
      color: #000;
      box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
      padding: 10px 20px;
      border-radius: 8px;
      font-size: 12px;
    }

    .notification-badge {
      position: absolute;
      top: -5px;
      right: -5px;
      background-color: red;
      color: white;
      border-radius: 50%;
      padding: 5px 10px;
      font-size: 14px;
      font-weight: bold;
    }
  `}
      </style>

      <Tooltip title="Invia un feedback" arrow>
        <IconButton
          className="feedback-button"
          color="primary"
          onClick={handleDrawerToggle}
          aria-label="Feedback"
          sx={{ fontSize: 35 }} // Imposta la dimensione dell'icona
        >
          <FeedbackIcon fontSize="inherit" />
        </IconButton>
      </Tooltip>

      <Tooltip title="Installa la nostra App" arrow>
        <IconButton
          className="download-button"
          onClick={installApp}
          aria-label="Installa App"
          style={{
            position: 'fixed',
            bottom: '55px', // Sposta l'icona in basso a sinistra
            zIndex: 1,
            color: 'dodgerblue', // Cambia il colore in "dodger blue"
          }}
        >
          <DownloadIcon fontSize="large" />
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        <Box
          role="presentation"
          sx={{ width: 250, padding: 2 }}
        >
          <Typography variant="h8" gutterBottom style={{ fontWeight: 'bold' }}>
            La tua opinione è importante
          </Typography>
          <Divider sx={{ my: 1 }} />
          <TextField
            multiline
            rows={16}
            variant="outlined"
            fullWidth
            label="Lasciaci qui il tuo feedback"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            sx={{ mb: 1, mt: 1 }}
            placeholder={`Ci impegniamo costantemente per poter migliorare la vostra esperienza insieme a noi.\n
Il feedback è fondamentale per poter perfezionare il servizio ad ogni aggiornamento.\n
Facci sapere, proprio qui, cosa ti piacerebbe aggiungessimo per facilitare e velocizzare il tuo lavoro.\n
Grazie per aver scelto\nStudio Romeo.`}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={handleFeedbackSubmit}
            fullWidth
          >
            Invia
          </Button>

          <Button
            variant="contained"
            color="success"
            fullWidth
            sx={{ mt: 2, backgroundColor: 'green' }}
            onClick={() => window.location.href = "/cli/feedform"}    >
            Feedback Form
          </Button>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 1, color: 'text.secondary', fontStyle: 'italic' }}
          >
            Entra qui per darci il tuo feedback sui nostri form disponibili.
          </Typography>
        </Box>
      </Drawer>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        action={<Button color="inherit" onClick={handleCloseSnackbar}>Chiudi</Button>}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showNotificationReminder}
        autoHideDuration={null}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => {
          setShowNotificationReminder(false);
          localStorage.setItem('notificationReminderShown', 'true');
        }}
        sx={{
          top: '50px !important',
          '& .MuiPaper-root': {
            borderRadius: '12px',
            width: '90%', // Imposta la stessa dimensione
            maxWidth: '400px', // Larghezza massima
          }
        }}
      >
        <Alert
          severity="info"
          onClose={() => {
            setShowNotificationReminder(false);
            localStorage.setItem('notificationReminderShown', 'true');
          }}
          icon={false}
          sx={{
            width: '100%',
            backgroundColor: '#333', // Colore scuro
            color: '#fff',
            fontWeight: 'bold',
            borderRadius: '12px',
            textAlign: 'center',
            p: 2
          }}
        >
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            🔔 Rimani sempre aggiornato dal nostro Studio! Attiva le notifiche cliccando sull'icona a sinistra della barra di navigazione, accanto al nome del sito.
          </Typography>
        </Alert>
      </Snackbar>

      <Snackbar
        open={showInstallPrompt && !!installEvent}
        autoHideDuration={null}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => {
          setShowInstallPrompt(false);
          setInstallEvent(null);
          localStorage.setItem('installPromptShown', 'true');
        }}
        sx={{
          top: '220px !important',
          '& .MuiPaper-root': {
            borderRadius: '12px',
            width: '90%', // Imposta la stessa dimensione
            maxWidth: '400px', // Larghezza massima
          }
        }}
      >
        <Alert
          severity="success"
          onClose={() => {
            setShowInstallPrompt(false);
            setInstallEvent(null);
            localStorage.setItem('installPromptShown', 'true');
          }}
          icon={false}
          sx={{
            width: '100%',
            backgroundColor: '#333', // Colore scuro
            color: '#fff',
            fontWeight: 'bold',
            borderRadius: '12px',
            textAlign: 'center',
            p: 2
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            📲 Installa la nostra App per un accesso ancora più rapido!
          </Typography>
          <Box display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="inherit"
              onClick={installApp}
              sx={{ borderRadius: '8px', backgroundColor: '#43a047', color: '#fff' }}
            >
              Installa App
            </Button>
          </Box>
        </Alert>
      </Snackbar>

      <Typography variant="h4" gutterBottom align="center" style={{ marginTop: '30px' }}>
        {`Benvenuto${ragioneSociale ? `, ${ragioneSociale}` : ''}`}
      </Typography>

      <Typography
        style={{
          textAlign: 'center',
          fontStyle: 'italic',
          color: '#9e9e9e',
          fontSize: '0.9rem',
          minHeight: '1.2em' // evita salto layout iniziale
        }}
      >
        {displayedText}
      </Typography>

      <Divider sx={{ my: 1 }} />

      <Container maxWidth="md" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '55vh' }}>
        <Box display="flex" flexWrap="wrap" justifyContent="space-between" className="clienti-area">
          {Object.keys(linkContentMap).map(key => renderLink(key))}
        </Box>
      </Container>

    </>
  );
}

export default Cli;